import { Button, Tabs, Tab, TextField, IconButton } from '@material-ui/core';
import { Attachment, Error, RemoveCircle } from '@material-ui/icons';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDropzone } from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import ReactTooltip from 'react-tooltip';
import { ASSET_URL, PROPERTY_SERVICE_URL, ENVIRONMENT } from '../../env/Configurations';
import { notificationManager } from '../../utils/NotificationUtils';
import DeletePopupModel from '../delete-popup/Delete-Popup';
import {
  RemoveCircleRounded,
  AddCircleOutline,
  DeleteOutline,
  SwapHoriz,
  DragIndicator,
  Loop,
} from '@material-ui/icons';
import './ImageUpload.scss';
import './ReactCrop.scss';
import { isEmpty } from 'lodash';
import CustomSelect from '../form-controls/custom-select/Custom-Select';
import {
  appendFileExtensionIfMissing,
  convertFileToBase64,
  calculateFileChecksum,
  calculateTotalChunks,
  CHUNK_SIZE,
  completeMultipartUpload,
  abortMultipartUpload,
  sanitizeFileName,
} from '../../utils/commonUtils';
import { ASSET_TYPE_PROPERTY } from '../../constants/PropertyConst';
import { getPreSignedUrl } from '../../utils/PropertyAPIUtils';
import { uploadGifToCMS } from '../../utils/mediaUtils';
import Loader from '../loader/Loader';

const MEDIA_TABS = { IMAGE: 'image', VIDEO: 'video', GIF: 'gif' };
const LOOP_TYPES = { ALWAYS: 'always', COUNT: 'count' };

const ComingSoonPane = ({ title, subtitle }) => (
  <div className='row pt-2'>
    <div
      className='img-drop-zone w-100 d-flex align-items-center justify-content-center'
      style={{ minHeight: 195, border: '1px dashed #CCCCCC', borderRadius: 10 }}
    >
      <div className='text-center'>
        <div className='mb-2' style={{ fontWeight: 600 }}>
          {title}
        </div>
        <div className='gray-text'>{subtitle}</div>
      </div>
    </div>
  </div>
);

const baseStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderRadius: 10,
  borderColor: '#CCCCCC',
  borderStyle: 'dashed',
  backgroundColor: '#fff',
  color: '#CCCCCC',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  minHeight: 195,
  maxWidth: 287,
  opacity: 1,
};
const activeStyle = { borderColor: '#2196f3' };
const acceptStyle = { borderColor: '#00e676' };
const rejectStyle = { borderColor: '#ff1744' };
const grid = 8;
const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  padding: 6,
  margin: `0 0 ${grid}px 0`,
  color: '#828282',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#FBFBFB',
  border: isDragging ? '2px solid #03E67C' : '2px solid #FBFBFB',
  ...draggableStyle,
});
const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? '#CFF8E0' : '#FFF',
  padding: grid,
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
});

const validateThumbFile = (file) => {
  const okTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!okTypes.includes(file?.type)) return 'Please upload a valid image file (JPG/PNG)';
  if (file.size > 10 * BYTES_IN_MB) return 'Image size must be 10 MB or less';
  return null;
};

const createThumbURL = (file, bin) => {
  const u = URL.createObjectURL(file);
  bin.current.add(u);
  return u;
};

const newVideoItem = () => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  url: '',
  uploadedAt: new Date().toISOString(),
  loopType: LOOP_TYPES.ALWAYS,
  loopCount: 0,
  thumbnail: '',
});
const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || '';
  }
};
const isDirectFile = (u = '') => /\.(mp4|webm|ogg|ogv|m4v|mov)(\?.*)?$/i.test(u.trim());
const parseTimeToSeconds = (t) => {
  if (!t) return 0;
  if (/^\d+$/.test(t)) return parseInt(t, 10);
  const m = String(t).match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
  if (!m) return 0;
  const [, h = 0, mnt = 0, s = 0] = m.map((x) => parseInt(x || '0', 10));
  return h * 3600 + mnt * 60 + s;
};
const getYouTubeStart = (urlObj) => {
  const t = urlObj.searchParams.get('t') || urlObj.searchParams.get('start');
  return parseTimeToSeconds(t);
};
const extractYouTubeId = (urlObj) => {
  const host = urlObj.hostname.replace(/^www\./, '');
  if (host === 'youtu.be') return urlObj.pathname.split('/').filter(Boolean)[0] || '';
  if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
    const p = urlObj.pathname.split('/').filter(Boolean);
    if (urlObj.searchParams.get('v')) return urlObj.searchParams.get('v');
    if (p[0] === 'shorts' && p[1]) return p[1];
    if (p[0] === 'embed' && p[1]) return p[1];
  }
  return '';
};

const extractVimeoId = (urlObj) => {
  const p = urlObj.pathname.split('/').filter(Boolean);
  if (p[0] === 'video' && p[1]) return p[1].replace(/\D/g, '');
  if (p[0]) return p[0].replace(/\D/g, '');
  return '';
};

const extractDailymotionId = (urlObj) => {
  const host = urlObj.hostname.replace(/^www\./, '');
  const p = urlObj.pathname.split('/').filter(Boolean);
  if (host === 'dai.ly' && p[0]) return p[0];
  if (host.endsWith('dailymotion.com') && p[0] === 'video' && p[1]) return p[1].split('_')[0];
  return '';
};

const buildTwitchEmbed = (urlObj) => {
  const p = urlObj.pathname.split('/').filter(Boolean);
  const parent = (typeof window !== 'undefined' && window.location.hostname) || 'localhost';
  if (p[0] === 'videos' && p[1]) {
    return `https://player.twitch.tv/?video=${encodeURIComponent(p[1])}&parent=${encodeURIComponent(
      parent,
    )}&autoplay=true`;
  }
  if (p[0]) {
    return `https://player.twitch.tv/?channel=${encodeURIComponent(
      p[0],
    )}&parent=${encodeURIComponent(parent)}&autoplay=true`;
  }
  return '';
};

const getVideoTypeLabel = (raw = '') => {
  if (!raw) return '';
  const m = raw.trim().match(/\.(mp4|webm|ogg|ogv|m4v|mov)(?:[?#].*)?$/i);
  if (m) return m[1].toLowerCase();
  try {
    const host = new URL(raw).hostname.replace(/^www\./, '');
    if (host.includes('youtube') || host === 'youtu.be') return 'youtube';
    if (host.includes('vimeo.com')) return 'vimeo';
    if (host.includes('dailymotion.com') || host === 'dai.ly') return 'dailymotion';
    if (host.includes('twitch.tv')) return 'twitch';
  } catch {}
  return 'unknown';
};
const isValidVideoFormat = (url) => {
  if (!url || !url.trim()) return true;

  const trimmedUrl = url.trim();
  let urlObj;
  try {
    urlObj = new URL(trimmedUrl);
  } catch {
    const fileMatch = trimmedUrl.match(/\.(mp4|webm)(?:[?#].*)?$/i);
    return !!fileMatch;
  }
  if (urlObj.protocol !== 'https:') {
    return false;
  }
  const host = urlObj.hostname.replace(/^www\./, '');
  if (
    host === 'youtu.be' ||
    host.endsWith('youtube.com') ||
    host.endsWith('youtube-nocookie.com') ||
    host === 'm.youtube.com'
  ) {
    return true;
  }
  const fileMatch = trimmedUrl.match(/\.(mp4|webm)(?:[?#].*)?$/i);
  return !!fileMatch;
};

const getVideoEmbed = (raw) => {
  if (!raw) return { kind: 'unknown' };
  if (isDirectFile(raw)) return { kind: 'file' };
  let urlObj;
  try {
    urlObj = new URL(raw);
  } catch {
    return { kind: 'unknown' };
  }
  if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
    return { kind: 'unknown' };
  }
  const host = urlObj.hostname.replace(/^www\./, '');
  if (
    host === 'youtu.be' ||
    host.endsWith('youtube.com') ||
    host.endsWith('youtube-nocookie.com') ||
    host === 'm.youtube.com'
  ) {
    const id = extractYouTubeId(urlObj);
    if (id) {
      const start = getYouTubeStart(urlObj);
      const embed = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
        id,
      )}?rel=0&modestbranding=1&autoplay=1&controls=1${start ? `&start=${start}` : ''}`;
      const thumb = `https://img.youtube.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
      return { kind: 'youtube', embed, thumb };
    }
  }
  if (host.endsWith('vimeo.com')) {
    const id = extractVimeoId(urlObj);
    if (id) {
      return { kind: 'vimeo', embed: `https://player.vimeo.com/video/${id}?autoplay=1` };
    }
  }
  if (host.endsWith('dailymotion.com') || host === 'dai.ly') {
    const id = extractDailymotionId(urlObj);
    if (id) {
      return {
        kind: 'dailymotion',
        embed: `https://www.dailymotion.com/embed/video/${id}?autoplay=1`,
      };
    }
  }
  if (host.endsWith('twitch.tv')) {
    const embed = buildTwitchEmbed(urlObj);
    if (embed) return { kind: 'twitch', embed };
  }
  if (isDirectFile(raw)) return { kind: 'file' };
  return { kind: 'unknown' };
};

const SupportedFormatsNote = ({ types, size, className = '', hideMaxSize = false }) => (
  <div className={`file-size-info mt-1 ${className}`}>
    <small>File type: {types}</small>
    {!hideMaxSize && <small>Max file size: {size}</small>}
  </div>
);

const VideoPreview = ({ url, uploadedAt, onError }) => {
  const [error, setError] = useState('');
  const onErrorRef = useRef(onError);
  const [showEmbed, setShowEmbed] = useState(false);
  const vref = useRef(null);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    setError('');
    onErrorRef.current && onErrorRef.current('');
    setShowEmbed(false);
  }, [url]);

  useEffect(() => {
    if (!url || !url.trim()) return;

    let cancelled = false;
    const validateVideo = async () => {
      try {
        const urlObj = new URL(url);
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
          if (!cancelled) {
            setError('Preview failed. File format or URL might be unsupported by the browser.');
          }
          return;
        }
        if (urlObj.protocol === 'http:') {
          if (!cancelled) {
            setError('Insecure HTTP URLs are not allowed. Please use HTTPS.');
          }
          return;
        }
        const host = urlObj.hostname.replace(/^www\./, '');
        if (
          host === 'youtu.be' ||
          host.endsWith('youtube.com') ||
          host.endsWith('youtube-nocookie.com') ||
          host === 'm.youtube.com'
        ) {
          const videoId = extractYouTubeId(urlObj);
          if (!videoId) {
            if (!cancelled) {
              setError('Invalid YouTube URL. Please provide a complete video URL.');
            }
            return;
          }

          try {
            const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
            const response = await fetch(oembedUrl);

            if (!response.ok && !cancelled) {
              setError('Video not found or unavailable. Please check the URL.');
            }
          } catch (err) {
            if (!cancelled) {
              setError('Video not found or unavailable. Please check the URL.');
            }
          }
        }
      } catch {}
    };

    validateVideo();

    return () => {
      cancelled = true;
    };
  }, [url]);
  useEffect(() => {
    onErrorRef.current && onErrorRef.current(error);
  }, [error]);
  const meta = getVideoEmbed(url);
  if (meta.kind === 'file') {
    return (
      <div>
        <div
          style={{
            position: 'relative',
            width: 320,
            height: 180,
            background: '#000',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <video
            ref={vref}
            src={url}
            width={320}
            height={180}
            controls
            preload='metadata'
            controlsList='nodownload'
            onError={() =>
              setError('Preview failed. File format or URL might be unsupported by the browser.')
            }
            style={{ display: 'block', background: '#000' }}
          />
        </div>
        {error && <div className='input-error mt-2'>{error}</div>}
      </div>
    );
  }
  const renderIframe = (src) => (
    <iframe
      title='Video preview'
      src={src}
      width='320'
      height='180'
      frameBorder='0'
      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
      allowFullScreen
      referrerPolicy='strict-origin-when-cross-origin'
      style={{ display: 'block' }}
      onError={() =>
        setError('Preview failed. This URL may be blocked by browser/CSP or is not embeddable.')
      }
    />
  );
  if (['youtube', 'vimeo', 'dailymotion', 'twitch'].includes(meta.kind)) {
    const isYouTube = meta.kind === 'youtube';
    return (
      <div>
        <div
          style={{
            position: 'relative',
            width: 320,
            height: 180,
            background: '#000',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          {isYouTube ? (
            !showEmbed ? (
              <button
                type='button'
                onClick={() => setShowEmbed(true)}
                className='pointer'
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundImage: meta.thumb
                    ? `url(${meta.thumb})`
                    : 'linear-gradient(135deg,#222,#111)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: 'none',
                  padding: 0,
                }}
                aria-label='Play video'
                title='Play video'
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderTop: '10px solid transparent',
                      borderBottom: '10px solid transparent',
                      borderLeft: '16px solid #fff',
                      marginLeft: 3,
                    }}
                  />
                </div>
              </button>
            ) : (
              renderIframe(meta.embed)
            )
          ) : (
            renderIframe(meta.embed)
          )}
        </div>
        {error && <div className='input-error mt-2'>{error}</div>}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          position: 'relative',
          width: 320,
          height: 180,
          background: '#000',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <video
          ref={vref}
          src={url}
          width={320}
          height={180}
          controls
          preload='metadata'
          controlsList='nodownload'
          onError={() =>
            setError('Preview failed. File format or URL might be unsupported by the browser.')
          }
          style={{ display: 'block', background: '#000' }}
        />
      </div>
      {error && <div className='input-error mt-2'>{error}</div>}
    </div>
  );
};
const validateGifUrl = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
const BYTES_IN_MB = 1048576;
const newGifItem = ({ file, url, gifUrl, uploading = false }) => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: sanitizeFileName(file?.name) || 'unnamed.gif',
  size: file?.size || 0,
  file: uploading ? file : null,
  url: gifUrl ? (gifUrl.startsWith('http') ? gifUrl : `${ASSET_URL}${gifUrl}`) : url,
  gifUrl: gifUrl || url,
  uploadedAt: new Date().toISOString(),
  thumbnail: { preview: '', key: '' },
  uploading: uploading,
});
const GifPreview = ({ url, uploadedAt, uploading = false, onError }) => {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    setErrored(false);
    setLoaded(false);
  }, [url]);
  useEffect(() => {
    onErrorRef.current && onErrorRef.current(!loaded || errored, errored);
  }, [errored, loaded]);
  return (
    <div>
      <div
        style={{
          position: 'relative',
          width: 320,
          height: 195,
          background: '#000',
          borderRadius: 10,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!errored ? (
          <img
            src={url}
            alt='GIF preview'
            width={320}
            height={180}
            style={{ objectFit: 'contain' }}
            onLoad={() => setLoaded(true)}
            onError={() => { setErrored(true); setLoaded(true); }}
          />
        ) : (
          <div className='gray-text' style={{ color: '#ccc' }}>
            Unable to preview GIF
          </div>
        )}
        {uploading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ height: '80px', width: '80px', transform: 'scale(0.8)' }}>
              <Loader />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const VideoThumbUploader = ({
  thumb,
  onChange,
  label = 'Video Thumbnail',
  tooltipTitle,
  uploading = false,
}) => {
  const [error, setError] = useState('');

  const onFiles = (files) => {
    const file = files[0];
    const err = validateThumbFile(file);
    if (err) return setError(err);
    setError('');
    onChange(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.files?.length) return;
    onFiles(e.dataTransfer.files);
  };

  const openPicker = () => document.getElementById('video-thumb-picker').click();
  const maxFileSize = () => {
    return (
      <div className='file-size-info mt-3'>
        <small>File type: .JPG,&nbsp;.PNG</small>
        <small> Max File Size: 10MB</small>
      </div>
    );
  };

  const tooltipText = tooltipTitle || 'Display image after GIF playback';

  return (
    <>
      <div
        className='d-flex align-items-center justify-content-between mt-4'
        style={{ maxWidth: 320 }}
      >
        <div className='d-flex align-items-center'>
          <span className='bg-image-label'>{label}</span>
          <Tooltip title={tooltipText} placement='right'>
            <InfoIcon style={{ fontSize: '14px' }} className='ms-2' />
          </Tooltip>
        </div>
        {thumb && (
          <button className='remove-link' onClick={() => onChange(null)}>
            Remove
          </button>
        )}
      </div>

      <div
        className='video-thumb-drop-zone mt-2'
        style={{
          border: '1px dashed #BDBDBD',
          borderRadius: 10,
          width: 320,
          height: 180,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={openPicker}
      >
        {thumb ? (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <img
              src={thumb}
              alt='Thumbnail preview'
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {uploading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    height: '80px',
                    width: '80px',
                    transform: 'scale(0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Loader />
                </div>
              </div>
            )}
          </div>
        ) : (
          <span className='gray-text'>
            <Attachment style={{ marginRight: 6 }} />
            Drag & drop thumbnail or <span style={{ color: '#29E67C' }}>Browse</span>
          </span>
        )}
        <input
          id='video-thumb-picker'
          type='file'
          accept='image/png,image/jpeg,image/jpg'
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files.length) {
              onFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />
      </div>

      {thumb && (
        <div className='d-flex justify-content-end mt-1' style={{ maxWidth: 320 }}>
          <button
            className='remove-link'
            onClick={(e) => {
              e.preventDefault();
              openPicker();
            }}
          >
            Replace
          </button>
        </div>
      )}

      {error && <div className='input-error mt-2'>{error}</div>}
      {maxFileSize()}
    </>
  );
};

const GifDropArea = ({ onAdd, onError, maxMB = 10 }) => {
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: 'image/gif',
    multiple: true,
    onDropAccepted: (files) => onAdd(files),
    onDropRejected: () =>
      onError(`Only .gif files allowed and size must be ≤ ${maxMB}MB.`),
  });

  const dzStyle = {
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragReject ? rejectStyle : {}),
    minHeight: 180,
    maxWidth: '100%',
    marginBottom: 20,
  };

  return (
    <>
      <div className='row pt-2'>
        <div className='img-drop-zone w-100'>
          <div {...getRootProps({ style: dzStyle })}>
            <input {...getInputProps()} />
            <div className='d-flex align-items-center'>
              <Attachment />
              <span className='ms-1'>
                Drag and drop or <span className='text-highlight pointer'>Browse</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const fmtSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ImageUpload = (props) => {
  const {
    videoImageUrl,
    onVideoImageUrlChange,
    gifImageUrl,
    onGifImageUrlChange,
    onVideoError,
    onThumbnailUploading,
    onVideoLoading,
    onGifLoading,
  } = props;
  const {
    defaultMediaTab = MEDIA_TABS.IMAGE,
    enabledMediaTabs = [MEDIA_TABS.IMAGE, MEDIA_TABS.VIDEO, MEDIA_TABS.GIF],
  } = props;
  const [mediaTab, setMediaTab] = useState(
    enabledMediaTabs.includes(defaultMediaTab) ? defaultMediaTab : MEDIA_TABS.IMAGE,
  );
  const videoThumbUrlBin = useRef(new Set());
  const [videoThumbPreview, setVideoThumbPreview] = useState('');
  const [gifThumbPreview, setGifThumbPreview] = useState('');
  const [videoThumbUploading, setVideoThumbUploading] = useState(false);
  const [gifThumbUploading, setGifThumbUploading] = useState(false);
  const [videoErrors, setVideoErrors] = useState({});
  const [videoFormatErrors, setVideoFormatErrors] = useState({});
  const [isVideoUrlChanging, setIsVideoUrlChanging] = useState(false);
  const videoUrlTimerRef = useRef(null);
  const onVideoErrorRef = useRef(onVideoError);
  const onThumbnailUploadingRef = useRef(onThumbnailUploading);
  const onVideoLoadingRef = useRef(onVideoLoading);
  const onGifLoadingRef = useRef(onGifLoading);
  const [gifError, setGifError] = useState('');
  const [gifPreviewErrors, setGifPreviewErrors] = useState({});
  const [gifActualErrors, setGifActualErrors] = useState({});

  const uploadSingleImageViaCMS = async ({ file, groupId, propertyType, hotelId }) => {
    const fileSize = file.size;
    if (fileSize < 2 * 1024 * 1024) {
      const base64 = await convertFileToBase64(file);
      const input = {
        contentLength: file.size,
        contentType: file.type,
        filename: sanitizeFileName(file.name),
        skipContentValidation: false,
        type: ASSET_TYPE_PROPERTY,
        contents: base64,
        groupId,
        propertyType,
      };

      const res = await getPreSignedUrl(input);
      if (!res?.data?.getPreSignUrlCMS?.key) {
        throw new Error('Failed to get CMS presign url');
      }
      return res.data.getPreSignUrlCMS.key;
    }
    const totalChunks = calculateTotalChunks(fileSize);
    let uploadId = null;

    if (ENVIRONMENT === 'dev') {
      console.log(
        `Starting multipart upload for file: ${file.name}, size: ${fileSize} bytes, chunks: ${totalChunks}`,
      );
    }

    try {
      const fileChecksum = await calculateFileChecksum(file);
      const startUploadResponse = await fetch(`${PROPERTY_SERVICE_URL}/startMultipartUpload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          groupId,
          type: ASSET_TYPE_PROPERTY,
          contentType: file.type,
          filename: sanitizeFileName(file.name),
          skipContentValidation: false,
          hotelId,
          propertyType,
          IsCMSRequest: true,
          fileSize,
          fileChecksum,
        }),
      });

      if (!startUploadResponse.ok) {
        throw new Error(`Failed to start upload: ${startUploadResponse.status}`);
      }

      const chunkData = await startUploadResponse.json();
      uploadId = chunkData?.data?.key;

      if (!uploadId) {
        throw new Error('Failed to get upload ID from server');
      }

      if (ENVIRONMENT === 'dev') {
        console.log('Upload started, uploadId:', uploadId);
      }
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileSize);
        const fileChunk = file.slice(start, end);

        const base64Chunk = await convertFileToBase64(fileChunk);
        const response = await fetch(`${PROPERTY_SERVICE_URL}/uploadMultipartChunk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            groupId,
            hotelId,
            uploadId,
            sequenceNumber: chunkIndex + 1,
            contents: base64Chunk,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to upload chunk ${chunkIndex + 1}: ${response.status}`);
        }

        const responseData = await response.json();
        if (ENVIRONMENT === 'dev') {
          console.log(`Chunk ${chunkIndex + 1}/${totalChunks} uploaded`);
        }
      }
      if (ENVIRONMENT === 'dev') {
        console.log('All chunks uploaded, completing multipart upload...');
        console.log('Calling completeMultipartUpload with:', { groupId, hotelId, uploadId });
      }

      const completeResponse = await completeMultipartUpload(groupId, hotelId, uploadId);

      if (ENVIRONMENT === 'dev') {
        console.log('Complete response:', JSON.stringify(completeResponse, null, 2));
      }

      if (completeResponse?.status === 'ok' && completeResponse?.data?.status === true) {
        const imageKey = completeResponse?.data?.message;
        if (!imageKey) {
          if (ENVIRONMENT === 'dev') {
            console.error('No image key in response:', completeResponse);
          }
          throw new Error('Failed to get image key from server');
        }
        if (ENVIRONMENT === 'dev') {
          console.log('Upload completed successfully, key:', imageKey);
        }
        return imageKey;
      } else {
        if (ENVIRONMENT === 'dev') {
          console.error('Complete upload failed. Response:', completeResponse);
        }
        throw new Error('Failed to complete upload');
      }
    } catch (error) {
      if (ENVIRONMENT === 'dev') {
        console.error('Error during multipart upload:', error);
      }

      if (uploadId) {
        if (ENVIRONMENT === 'dev') {
          console.log('Aborting multipart upload with uploadId:', uploadId);
        }
        try {
          await abortMultipartUpload(groupId, hotelId, uploadId);
          if (ENVIRONMENT === 'dev') {
            console.log('Multipart upload aborted successfully');
          }
        } catch (abortError) {
          if (ENVIRONMENT === 'dev') {
            console.error('Error aborting multipart upload:', abortError);
          }
        }
      }
      throw error;
    }
  };

  const resolveGifSrc = (gif) => {
    if (!gif) return '';
    const primaryPath = gif.gifUrl || '';
    if (/^(https?:|blob:)/i.test(primaryPath)) return primaryPath;
    const fallbackPath = gif.url || '';
    if (/^(https?:|blob:)/i.test(fallbackPath)) return fallbackPath;
    return primaryPath ? `${ASSET_URL}${primaryPath}` : '';
  };

  const hasAnyVideoUrl = (list = []) => list.some((v) => v.url?.trim());

  const clearVideoThumbnail = () => {
    try {
      videoThumbUrlBin.current.forEach((u) => URL.revokeObjectURL(u));
    } catch {}
    videoThumbUrlBin.current.clear();
    setVideoThumbPreview('');
    onVideoImageUrlChange && onVideoImageUrlChange('');
  };

  const clearGifThumbnail = () => {
    setGifThumbPreview('');
    onGifImageUrlChange && onGifImageUrlChange('');
  };

  const addGifFiles = async (files) => {
    const maxBytes = (allowedGifSizeMB || 10) * BYTES_IN_MB;
    const bad = files.find(
      (f) => !(f.type === 'image/gif' || /\.gif$/i.test(f.name)) || f.size > maxBytes,
    );
    if (bad) {
      setGifError(`Only .gif files allowed and size must be ≤ ${allowedGifSizeMB}MB.`);
      return;
    }
    setGifError('');

    const tempGifItems = files.map((file) =>
      newGifItem({ file, url: createGifURL(file), uploading: true }),
    );
    const nextWithUploading = [...gifList, ...tempGifItems];
    pushGifUpdate(nextWithUploading);
    setSelectedGifId(nextWithUploading[nextWithUploading.length - 1]?.id || null);

    for (let i = 0; i < tempGifItems.length; i++) {
      const gifItem = tempGifItems[i];
      const file = files[i];

      try {
        if (ENVIRONMENT === 'dev') {
          console.log(`Uploading GIF: ${file.name}`);
        }

        const uploadedKey = await uploadGifToCMS({
          file,
          groupId: props.groupId,
          propertyType: props.propertyType,
          hotelId: props.hotelId,
        });

        if (ENVIRONMENT === 'dev') {
          console.log(`GIF uploaded successfully: ${uploadedKey}`);
        }
        setGifList((currentList) => {
          const updatedList = currentList.map((g) =>
            g.id === gifItem.id
              ? {
                  ...g,
                  gifUrl: uploadedKey,
                  url: uploadedKey.startsWith('http') ? uploadedKey : `${ASSET_URL}${uploadedKey}`,
                  file: null,
                  uploading: false,
                }
              : g,
          );
          props.onGifListUpdated && props.onGifListUpdated(updatedList);
          return updatedList;
        });
        const serverUrl = uploadedKey.startsWith('http') ? uploadedKey : `${ASSET_URL}${uploadedKey}`;
        const isValid = await validateGifUrl(serverUrl);
        setGifPreviewErrors((prev) => ({ ...prev, [gifItem.id]: !isValid }));
        setGifActualErrors((prev) => ({ ...prev, [gifItem.id]: !isValid }));

        revokeGifURL(gifItem.url);
      } catch (error) {
        if (ENVIRONMENT === 'dev') {
          console.error(`Error uploading GIF ${file.name}:`, error);
        }
        setGifList((currentList) => {
          const updatedList = currentList.filter((g) => g.id !== gifItem.id);
          props.onGifListUpdated && props.onGifListUpdated(updatedList);
          return updatedList;
        });
        revokeGifURL(gifItem.url);

        notificationManager('danger', `Failed to upload ${file.name}`);
      }
    }
  };

  const onMediaTabChange = (_e, value) => {
    setMediaTab(value);
    if (value !== MEDIA_TABS.IMAGE) {
      setImageError({ status: false, message: '' });
      setEditingImage({ index: -1, image: null, url: null });
    }
    if (value !== MEDIA_TABS.GIF) {
      setGifError('');
    }
  };
  const withLoopDefaults = (list = []) =>
    list.map((v) => {
      const loopType = v.loopType ?? LOOP_TYPES.ALWAYS;
      let loopCount = v.loopCount;

      if (loopType !== LOOP_TYPES.COUNT) {
        loopCount = 0;
      } else if (loopCount === '' || loopCount === null || typeof loopCount === 'undefined') {
        loopCount = '';
      } else {
        const n = Math.trunc(Number(loopCount));
        loopCount = Number.isFinite(n) && n >= 1 ? n : 1;
      }

      return { ...v, loopType, loopCount };
    });

  const isVideoControlled =
    Array.isArray(props.videoList) && typeof props.onVideoListUpdated === 'function';
  const [videoList, setVideoList] = useState(() =>
    isVideoControlled
      ? props.videoList.length
        ? withLoopDefaults(props.videoList)
        : [newVideoItem()]
      : [newVideoItem()],
  );
  const [selectedVideoId, setSelectedVideoId] = useState(() => videoList[0]?.id);
  const [loopType, setLoopType] = useState(LOOP_TYPES.ALWAYS);
  const [loopCount, setLoopCount] = useState(1);
  useEffect(() => {
    if (isVideoControlled) {
      setVideoList(props.videoList.length ? withLoopDefaults(props.videoList) : [newVideoItem()]);
      if (props.videoList.length) {
        setSelectedVideoId(
          (prev) => props.videoList.find((v) => v.id === prev)?.id || props.videoList[0].id,
        );
      }
    }
  }, [props.videoList]);
  useEffect(() => {
    onVideoErrorRef.current = onVideoError;
  }, [onVideoError]);
  useEffect(() => {
    if (onVideoErrorRef.current) {
      const hasPreviewError = Object.values(videoErrors).some((hasError) => hasError);
      const hasFormatError = Object.values(videoFormatErrors).some((hasError) => hasError);
      onVideoErrorRef.current(hasPreviewError || hasFormatError);
    }
  }, [videoErrors, videoFormatErrors]);
  useEffect(() => {
    onThumbnailUploadingRef.current = onThumbnailUploading;
  }, [onThumbnailUploading]);
  useEffect(() => {
    if (onThumbnailUploadingRef.current) {
      const isUploading = videoThumbUploading || gifThumbUploading;
      onThumbnailUploadingRef.current(isUploading);
    }
  }, [videoThumbUploading, gifThumbUploading]);
  useEffect(() => {
    onVideoLoadingRef.current = onVideoLoading;
  }, [onVideoLoading]);
  useEffect(() => {
    if (onVideoLoadingRef.current) {
      onVideoLoadingRef.current(isVideoUrlChanging);
    }
  }, [isVideoUrlChanging]);
  useEffect(() => {
    onGifLoadingRef.current = onGifLoading;
  }, [onGifLoading]);

  const pushVideoUpdate = (next) => {
    const resequenced = next.map((item, idx) => ({
      ...item,
      index: idx,
    }));

    setVideoList(resequenced);
    props.onVideoListUpdated && props.onVideoListUpdated(resequenced);
  };

  const addVideoRow = () => {
    const next = [...videoList, newVideoItem()];
    pushVideoUpdate(next);
    setSelectedVideoId(next[next.length - 1].id);
    setTimeout(() => {
      const el = document.getElementById(`video-url-${next[next.length - 1].id}`);
      el && el.focus();
    }, 0);
  };

  const patchSelectedVideo = (patch) => {
    if (!selectedVideoId) return;
    const next = videoList.map((v) => (v.id === selectedVideoId ? { ...v, ...patch } : v));
    pushVideoUpdate(next);
  };
  const handleLoopTypeChange = (f) => {
    const next = f?.value;
    if (next === LOOP_TYPES.COUNT) {
      const current = videoList.find((v) => v.id === selectedVideoId);
      const safeCount = current && Number(current.loopCount) >= 1 ? Number(current.loopCount) : 1;
      patchSelectedVideo({ loopType: next, loopCount: safeCount });
    } else {
      patchSelectedVideo({ loopType: LOOP_TYPES.ALWAYS, loopCount: 0 });
    }
  };

  const handleLoopCountChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      patchSelectedVideo({ loopCount: '' });
      return;
    }
    const n = Math.trunc(Number(raw));
    patchSelectedVideo({ loopCount: Number.isFinite(n) ? Math.max(1, n) : 1 });
  };

  const clampLoopCountOnBlur = () => {
    const current = videoList.find((v) => v.id === selectedVideoId);
    if (!current) return;
    const n = Math.trunc(Number(current.loopCount));
    if (!Number.isFinite(n) || n < 1) patchSelectedVideo({ loopCount: 1 });
  };

  const deleteVideoRow = (id) => {
    const idx = videoList.findIndex((v) => v.id === id);
    const next = videoList.filter((v) => v.id !== id);
    const noUrlsLeft = !hasAnyVideoUrl(next);
    pushVideoUpdate(next.length ? next : [newVideoItem()]);
    if (!next.length) setSelectedVideoId(null);
    else setSelectedVideoId(next[Math.min(idx, next.length - 1)].id);
    if (noUrlsLeft) clearVideoThumbnail();
  };

  const replaceVideoRow = (id) => {
    const next = videoList.map((v) =>
      v.id === id ? { ...v, url: '', uploadedAt: new Date().toISOString() } : v,
    );
    pushVideoUpdate(next);
    if (!hasAnyVideoUrl(next)) clearVideoThumbnail();
    setSelectedVideoId(id);
    setTimeout(() => {
      const el = document.getElementById(`video-url-${id}`);
      el && el.focus();
    }, 0);
  };

  const updateVideoUrl = (id, url) => {
    const next = videoList.map((v) =>
      v.id === id ? { ...v, url, uploadedAt: v.url ? v.uploadedAt : new Date().toISOString() } : v,
    );
    pushVideoUpdate(next);
    if (!hasAnyVideoUrl(next)) clearVideoThumbnail();
    const isValid = isValidVideoFormat(url);
    setVideoFormatErrors((prev) => ({
      ...prev,
      [id]: !isValid && url.trim() !== '',
    }));
    if (url.trim()) {
      if (videoUrlTimerRef.current) {
        clearTimeout(videoUrlTimerRef.current);
      }
      setIsVideoUrlChanging(true);
      videoUrlTimerRef.current = setTimeout(() => {
        setIsVideoUrlChanging(false);
      }, 2000);
    }
  };

  const onVideoDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(videoList);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    pushVideoUpdate(items);
  };

  const selectedVideo = videoList.find((v) => v.id === selectedVideoId && v.url?.trim());
  useEffect(() => {
    const currentVideoIds = new Set(videoList.filter((v) => v.url?.trim()).map((v) => v.id));
    setVideoErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      Object.keys(newErrors).forEach((videoId) => {
        if (!currentVideoIds.has(videoId)) {
          delete newErrors[videoId];
        }
      });
      return newErrors;
    });
    setVideoFormatErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      Object.keys(newErrors).forEach((videoId) => {
        if (!currentVideoIds.has(videoId)) {
          delete newErrors[videoId];
        }
      });
      return newErrors;
    });
  }, [videoList]);

  useEffect(() => {
    const hasLoopCountVideo = videoList.some(
      (v) => (v.loopType ?? LOOP_TYPES.ALWAYS) === LOOP_TYPES.COUNT && v.url && v.url.trim() !== '',
    );

    if (!hasLoopCountVideo) {
      try {
        videoThumbUrlBin.current.forEach((u) => {
          try {
            URL.revokeObjectURL(u);
          } catch {}
        });
        videoThumbUrlBin.current.clear();
      } catch {}

      setVideoThumbPreview('');
      if (onVideoImageUrlChange) {
        onVideoImageUrlChange('');
      }
    }
  }, [videoList, onVideoImageUrlChange]);

  const [imageError, setImageError] = useState({ status: false, message: '' });
  const [editingImage, setEditingImage] = useState({ index: -1, image: null, url: null });
  const [crop, setCrop] = useState({ unit: '%', height: 80, x: 25, y: 10, aspect: 1 / 1 });
  const [deleteImage, setDeleteImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectdCount, setSelectedCount] = useState(0);
  const cropImgRef = useRef(null);
  const [selectedRatio, setSelectedRatio] = useState('ratio1to1');
  const [selectedValue, setSelectedValue] = useState(1 / 1);
  const genericError = 'Selected images are not fulfilling the requirements';
  const [editingCropParams, setEditingCropParams] = useReducer((state, s) => ({ ...state, ...s }), {
    ratio1to1: null,
    ratio16to9: null,
    ratio21to9: null,
    ratio9to16: null,
    ratio9to21: null,
  });

  useEffect(() => {
    if (props?.isSingleImage && props?.imgList[0]) {
      setEditingImage({
        index: 0,
        image: props?.imgList[0],
        url: props?.imgList[0].fileData.master.imgUrl,
      });
    }
  }, [props?.isSingleImage, props.imgList]);

  const onDropRejected = useCallback((rejectedFiles) => {
    const multipleSelected = rejectedFiles.length > 1;
    if (rejectedFiles && rejectedFiles.length > 0) {
      setImageError({
        status: true,
        message: multipleSelected ? genericError : 'Please add images with supported File formats',
      });
    }
  }, []);

  const onDropAccepted = (acceptedFiles) => {
    if (!isEmpty(props?.isMenuUrl)) {
      return setImageError({
        status: true,
        message:
          'Sorry! Unable to add image(s) while menu document exists. Please remove the document and try again.',
      });
    }
    if (editingImage.image && !props?.isSingleImage) {
      return setImageError({ status: true, message: 'Not allowed while cropping in progress' });
    }
    if (acceptedFiles && acceptedFiles.length > 0) {
      const multipleSelected = acceptedFiles.length > 1;
      setSelectedCount(acceptedFiles.length);
      setSelectedImages([]);
      setImageError({ status: false, message: '' });
      acceptedFiles.forEach((file) => {
        if (file.size < (props.allowedImageSize ? props.allowedImageSize * 1048576 : 10485761)) {
          const reader = new FileReader();
          reader.addEventListener('load', () => {
            const imageObj = new Image();
            imageObj.src = reader.result;
            imageObj.onload = async () => {
              if (
                imageObj?.height > (props?.resolutionHeight || 2800) ||
                imageObj?.width > (props?.resolutionWidth || 4096)
              ) {
                return setImageError({
                  status: true,
                  message: multipleSelected
                    ? genericError
                    : 'Sorry! Unable to add Image with dimension more than ' +
                      (props.resolutionWidth || 4096) +
                      ' x ' +
                      (props.resolutionHeight || 2800),
                });
              }
              const [master, ratio1to1, ratio16to9, ratio21to9, ratio9to16, ratio9to21] =
                await Promise.all([
                  dataURLtoFile(reader.result, 'master', imageObj.src),
                  cropImage('ratio1to1', imageObj, {
                    width:
                      imageObj.width < imageObj.height
                        ? imageObj.width * 0.8
                        : imageObj.height * 0.8,
                    height:
                      imageObj.height < imageObj.width
                        ? imageObj.height * 0.8
                        : imageObj.width * 0.8,
                    x: 0,
                    y: 0,
                  }),
                  cropImage('ratio16to9', imageObj, {
                    width: imageObj.width * (16 / 20),
                    height: (imageObj.width * (16 / 20)) / (16 / 9),
                    x: imageObj.width / 2 - (imageObj.width * (16 / 20)) / 2,
                    y: imageObj.height / 2 - (imageObj.width * (16 / 20)) / (16 / 9) / 2,
                  }),
                  cropImage('ratio21to9', imageObj, {
                    width: imageObj.width * (21 / 30),
                    height: (imageObj.width * (21 / 30)) / (21 / 9),
                    x: imageObj.width / 2 - (imageObj.width * (21 / 25)) / 2,
                    y: imageObj.height / 2 - (imageObj.width * (21 / 25)) / (21 / 9) / 2,
                  }),
                  cropImage('ratio9to16', imageObj, {
                    width: imageObj.height * (9 / 16),
                    height: (imageObj.height * (9 / 16)) / (9 / 16),
                    x: imageObj.height / 2 - (imageObj.height * (9 / 16)) / 2,
                    y: imageObj.height / 2 - (imageObj.height * (9 / 16)) / (9 / 16) / 2,
                  }),
                  cropImage('ratio9to21', imageObj, {
                    width: imageObj.height * (9 / 21),
                    height: (imageObj.height * (9 / 21)) / (9 / 21),
                    x: imageObj.height / 2 - (imageObj.height * (9 / 21)) / 2,
                    y: imageObj.height / 2 - (imageObj.height * (9 / 21)) / (9 / 21) / 2,
                  }),
                ]);

              const push = (imgObj) => setSelectedImages((prev) => [...prev, imgObj]);
              const imgObj = {
                img: imageObj,
                fileName: sanitizeFileName(file.name),
                crop: { master, ratio1to1, ratio16to9, ratio21to9, ratio9to16, ratio9to21 },
              };
              if (!props?.isSingleImage) push(imgObj);
              else setSelectedImages([imgObj]);
            };
          });
          reader.readAsDataURL(file);
          setImageError({ status: false, message: '' });
        } else {
          setImageError({
            status: true,
            message: multipleSelected
              ? genericError
              : ` Sorry! Unable to add Image with size more than ${
                  props.allowedImageSize || '10'
                }MB`,
          });
        }
      });
    }
  };

  const cropImage = (cropRatio, image, crop) =>
    new Promise((resolve, reject) => {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      let w = crop.width * scaleX,
        h = crop.height * scaleY;
      let canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, w, h, 0, 0, w, h);
      const MAX_SIDE = 1920;
      if (Math.max(w, h) > MAX_SIDE) {
        const shrink = MAX_SIDE / Math.max(w, h);
        const tmpW = Math.round(w * shrink),
          tmpH = Math.round(h * shrink);
        const tmp = document.createElement('canvas');
        tmp.width = tmpW;
        tmp.height = tmpH;
        tmp.getContext('2d').drawImage(canvas, 0, 0, tmpW, tmpH);
        canvas = tmp;
      }
      canvas.toBlob(
        async (blob) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = async () => {
            const fileObj = await dataURLtoFile(reader.result, cropRatio, reader.result);
            resolve(fileObj);
          };
          reader.onerror = reject;
        },
        'image/jpeg',
        0.6,
      );
    });

  const dataURLtoFile = (dataurl, filename, imgUrl) =>
    new Promise((resolve) => resolve({ file: createFileFromBase64(dataurl, filename), imgUrl }));

  useEffect(() => {
    if (selectdCount > 0 && selectedImages.length === selectdCount) {
      let cur = [...props.imgList];
      selectedImages.forEach((image) => {
        const currObj = {
          index: cur?.length,
          isTouched: true,
          fileName: image.fileName,
          fileData: { ...image.crop },
        };
        if (!props?.isSingleImage) cur.push(currObj);
        else cur[0] = currObj;
      });
      cur = cur.map((img, idx) => ({ ...img, index: idx }));
      props.onImageListUpdated(cur);
    }
  }, [selectedImages, selectdCount]);

  useEffect(() => {
    if (props.imgList.length === 0) {
      props.onImagesLoading(true);
      (async () => {
        try {
          const upImages = [...props.uploadedImages];
          let cur = [...props.imgList];
          const cropPromiseArr = upImages
            .sort((v1, v2) => v1.index - v2.index)
            .map(
              (image) =>
                new Promise(async (resolve, reject) => {
                  try {
                    const [
                      masterBase64,
                      ratio1to1Base64,
                      ratio16to9Base64,
                      ratio21to9Base64,
                      ratio9to16Base64,
                      ratio9to21Base64,
                    ] = await Promise.all([
                      convertToBase64(ASSET_URL + image.master),
                      convertToBase64(ASSET_URL + image.ratio1to1),
                      convertToBase64(ASSET_URL + image.ratio16to9),
                      convertToBase64(ASSET_URL + image.ratio21to9),
                      convertToBase64(ASSET_URL + image.ratio9to16),
                      convertToBase64(ASSET_URL + image.ratio9to21),
                    ]);

                    const currObj = {
                      isTouched: false,
                      fileName: image.fileName,
                      index: image.index,
                      master: image.master,
                      ratio1to1: image.ratio1to1,
                      ratio16to9: image.ratio16to9,
                      ratio21to9: image.ratio21to9,
                      ratio9to16: image.ratio9to16,
                      ratio9to21: image.ratio9to21,
                      fileData: {
                        master: {
                          file: masterBase64 ? createFileFromBase64(masterBase64, 'master') : null,
                          imgUrl: masterBase64 || null,
                        },
                        ratio1to1: {
                          file: ratio1to1Base64
                            ? createFileFromBase64(ratio1to1Base64, 'ratio1to1')
                            : null,
                          imgUrl: ratio1to1Base64 || null,
                        },
                        ratio16to9: {
                          file: ratio16to9Base64
                            ? createFileFromBase64(ratio16to9Base64, 'ratio16to9')
                            : null,
                          imgUrl: ratio16to9Base64 || null,
                        },
                        ratio21to9: {
                          file: ratio21to9Base64
                            ? createFileFromBase64(ratio21to9Base64, 'ratio21to9')
                            : null,
                          imgUrl: ratio21to9Base64 || null,
                        },
                        ratio9to16: {
                          file: ratio9to16Base64
                            ? createFileFromBase64(ratio9to16Base64, 'ratio9to16')
                            : null,
                          imgUrl: ratio9to16Base64 || null,
                        },
                        ratio9to21: {
                          file: ratio9to21Base64
                            ? createFileFromBase64(ratio9to21Base64, 'ratio9to21')
                            : null,
                          imgUrl: ratio9to21Base64 || null,
                        },
                      },
                    };
                    resolve(currObj);
                  } catch {
                    resolve(null);
                    props.onImagesLoading(false);
                  }
                }),
            );

            const results = await Promise.all(cropPromiseArr);
          const validResults = results.filter((res) => res !== null);
          const updatedList = [...cur, ...validResults];
          props.onImagesLoading(false);
          props.onImageListUpdated(updatedList);
        } catch (e) {
          props.onImageListUpdated([...props.uploadedImages]);
          props.onImagesLoading(false);
          notificationManager('danger', 'Uploaded image retrieval failed');
        }
      })();
    }
  }, [props.uploadedImages]);

  const createFileFromBase64 = (source, fileName) => {
    var arr = source.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const normalizedFileName = appendFileExtensionIfMissing(fileName, mime);
    return new File([u8arr], normalizedFileName, { type: mime });
  };

  const convertToBase64 = async (url) =>
    new Promise((resolve) => {
      fetch(url)
        .then(async (data) => {
          if (data.status === 200) {
            const blob = await data.blob();
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => resolve(reader.result);
          } else {
            resolve(false);
          }
        })
        .catch(() => resolve(false));
    });

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept: 'image/png, image/jpg, image/jpeg',
      multiple: true,
      onDropAccepted,
      onDropRejected,
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  const onLoad = useCallback((img) => {
    cropImgRef.current = img;
    setCrop({ aspect: 1 / 1 });
  }, []);
  const handleCropImageSelect = (image, index) => {
    setImageError({ status: false, message: '' });
    setEditingImage({ index, image, url: image.fileData.master.imgUrl });
  };

  const handleSaveCrop = async (img) => {
    try {
      const [ratio1to1, ratio16to9, ratio21to9, ratio9to16, ratio9to21] = await Promise.all([
        cropImage('ratio1to1', img, { ...editingCropParams.ratio1to1 }),
        cropImage('ratio16to9', img, { ...editingCropParams.ratio16to9 }),
        cropImage('ratio21to9', img, { ...editingCropParams.ratio21to9 }),
        cropImage('ratio9to16', img, { ...editingCropParams.ratio9to16 }),
        cropImage('ratio9to21', img, { ...editingCropParams.ratio9to21 }),
      ]);
      const curCroppedImages = [...props.imgList];
      props.onImageListUpdated(
        curCroppedImages.map((obj) => {
          if (obj.index === editingImage.index) {
            return {
              isTouched: true,
              fileName: editingImage.image.fileName,
              index: editingImage.index,
              master: obj.master,
              ratio1to1: obj.ratio1to1,
              ratio16to9: obj.ratio16to9,
              ratio21to9: obj.ratio21to9,
              ratio9to16: obj.ratio9to16,
              ratio9to21: obj.ratio9to21,
              fileData: {
                master: editingImage.image.fileData.master,
                ratio1to1,
                ratio16to9,
                ratio21to9,
                ratio9to16,
                ratio9to21,
              },
            };
          }
          return obj;
        }),
      );
      setImageError({ status: false, message: '' });
      if (props?.isSingleImage) {
        setEditingImage({
          index: 0,
          image: {
            isTouched: true,
            fileName: editingImage.image.fileName,
            index: editingImage.index,
            master: curCroppedImages[0].fileData.master,
            ratio1to1: curCroppedImages[0].ratio1to1,
            ratio16to9: curCroppedImages[0].ratio16to9,
            ratio21to9: curCroppedImages[0].ratio21to9,
            ratio9to16: curCroppedImages[0].ratio9to16,
            ratio9to21: curCroppedImages[0].ratio9to21,
            fileData: {
              master: editingImage.image.fileData.master,
              ratio1to1,
              ratio16to9,
              ratio21to9,
              ratio9to16,
              ratio9to21,
            },
          },
          url: curCroppedImages[0].fileData.master.imgUrl,
        });
      } else {
        setEditingImage({ index: -1, image: null, url: null });
      }
    } catch {
      setImageError({ status: true, message: 'Error during cropping. Please try again.' });
    }
  };

  const removeCroppedImg = () => {
    let updatedList;
    if (props?.isSingleImage) {
      updatedList = [];
      setEditingImage({ index: -1, image: null, url: null });
    } else {
      updatedList = props?.imgList
        ?.filter((img) => img?.index !== deleteImage?.index)
        ?.sort((a, b) => a?.index - b?.index)
        ?.map((img, idx) => ({ ...img, index: idx }));
    }
    props.onImageListUpdated(updatedList);
    setDeleteImage({
      index: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
    });
  };

  const onDeleteImageSelect = (image, type) => {
    setEditingImage({ index: -1, image: null, url: null });
    setDeleteImage({
      index: image.index,
      name: image.fileName,
      image: type === 'new' ? image?.fileData?.ratio1to1?.imgUrl : ASSET_URL + image?.ratio1to1,
      title: 'Are you sure you want to delete',
      description: 'This action cannot be reversed.',
      status: true,
      anchorEl: null,
      type,
    });
  };

  const onDeleteCancel = () =>
    setDeleteImage({
      index: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
    });

  const onDefaultCropperChange = (aspectRatio) => {
    if (!cropImgRef?.current) return;
    const key =
      aspectRatio === 16 / 9
        ? 'ratio16to9'
        : aspectRatio === 21 / 9
        ? 'ratio21to9'
        : aspectRatio === 1 / 1
        ? 'ratio1to1'
        : aspectRatio === 9 / 21
        ? 'ratio9to21'
        : 'ratio9to16';
    const cropVal = editingCropParams[key];
    if (!cropVal) {
      return;
    }
    setCrop({
      unit: 'px',
      x: cropVal.x,
      y: cropVal.y,
      width: cropVal.width,
      aspect: cropVal.aspectRatio,
    });
  };
  const onCropDragComplete = (c, field) => {
    const cropParams = { ...editingCropParams }[field];
    if (cropParams) {
      cropParams.width = c.width;
      cropParams.height = c.height;
      cropParams.x = c.x;
      cropParams.y = c.y;
      setEditingCropParams(cropParams);
    }
  };

  useEffect(() => {
    if (editingImage.url && cropImgRef?.current && !editingCropParams.ratio1to1) {
      const val = cropImgRef?.current?.height;
      setCrop({
        unit: 'px',
        x:
          cropImgRef?.current?.width / 2 -
          ((cropImgRef?.current?.width < cropImgRef?.current?.height
            ? cropImgRef?.current?.width
            : cropImgRef?.current?.height) *
            0.8) /
            2,
        y:
          val / 2 -
          ((cropImgRef?.current?.width < cropImgRef?.current?.height
            ? cropImgRef?.current?.width
            : cropImgRef?.current?.height) *
            0.8) /
            2,
        width:
          cropImgRef?.current?.width < cropImgRef?.current?.height
            ? cropImgRef?.current?.width * 0.8
            : cropImgRef?.current?.height * 0.8,
        aspect: 1 / 1,
      });

      setEditingCropParams({
        ratio1to1: {
          title: '1/1',
          upImg: cropImgRef?.current,
          aspectRatio: 1 / 1,
          width:
            cropImgRef?.current?.width < cropImgRef?.current?.height
              ? cropImgRef?.current?.width * 0.8
              : cropImgRef?.current?.height * 0.8,
          height:
            cropImgRef?.current?.width < cropImgRef?.current?.height
              ? cropImgRef?.current?.width * 0.8
              : cropImgRef?.current?.height * 0.8,
          x: cropImgRef?.current?.width / 2 - (cropImgRef?.current?.width * 0.8) / 2,
          y: val / 2 - (cropImgRef?.current?.width * 0.8) / 2,
        },
        ratio16to9: {
          title: '16/9',
          upImg: cropImgRef?.current,
          aspectRatio: 16 / 9,
          width: cropImgRef?.current?.width * (16 / 20),
          height: (cropImgRef?.current?.width * (16 / 20)) / (16 / 9),
          x: cropImgRef?.current?.width / 2 - (cropImgRef?.current?.width * (16 / 20)) / 2,
          y: val / 2 - (cropImgRef?.current?.width * (16 / 20)) / (16 / 9) / 2,
        },
        ratio21to9: {
          title: '21/9',
          upImg: cropImgRef?.current,
          aspectRatio: 21 / 9,
          width: cropImgRef?.current?.width * (21 / 30),
          height: (cropImgRef?.current?.width * (21 / 30)) / (21 / 9),
          x: cropImgRef.current?.width / 2 - (cropImgRef.current?.width * (21 / 30)) / 2,
          y: val / 2 - (cropImgRef?.current?.width * (21 / 30)) / (21 / 9) / 2,
        },
        ratio9to16: {
          title: '9/16',
          upImg: cropImgRef?.current,
          aspectRatio: 9 / 16,
          width: cropImgRef?.current?.height * (9 / 16),
          height: (cropImgRef?.current?.height * (9 / 16)) / (9 / 16),
          x: cropImgRef?.current?.height / 2 - (cropImgRef?.current?.height * (9 / 16)) / 2,
          y: val / 2 - (cropImgRef?.current?.height * (9 / 16)) / (9 / 16) / 2,
        },
        ratio9to21: {
          title: '9/21',
          upImg: cropImgRef?.current,
          aspectRatio: 9 / 21,
          width: cropImgRef?.current?.height * (9 / 21),
          height: (cropImgRef?.current?.height * (9 / 21)) / (9 / 21),
          x: cropImgRef?.current?.height / 2 - (cropImgRef?.current?.height * (9 / 21)) / 2,
          y: val / 2 - (cropImgRef?.current?.height * (9 / 21)) / (9 / 21) / 2,
        },
      });
    }
  }, [editingImage.url, cropImgRef?.current]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(props.imgList.sort((v1, v2) => v1.index - v2.index));
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    const resequenced = items.map((item, key) => ({ ...item, index: key }));
    props.onImageListUpdated(resequenced);
  };

  const handleRatioChange = (ratio) => {
    setSelectedRatio(ratio);
    onDefaultCropperChange(ratio);
  };
  const imageRatios = [
    { value: 1 / 1, label: '1:1' },
    { value: 16 / 9, label: '16:9' },
    { value: 9 / 16, label: '9:16' },
    { value: 21 / 9, label: '21:9' },
    { value: 9 / 21, label: '9:21' },
  ];
  const handleInputChange = (field) => {
    const val = parseFloat(field.value);
    setSelectedValue(val);
    handleRatioChange(val);
  };
  const { allowedGifSizeMB = 10 } = props;

  const isGifControlled =
    Array.isArray(props.gifList) && typeof props.onGifListUpdated === 'function';
  const [gifList, setGifList] = useState(() => (isGifControlled ? props.gifList || [] : []));
  const [selectedGifId, setSelectedGifId] = useState(() => gifList[0]?.id || null);
  const gifUrlBin = useRef(new Set());

  useEffect(() => {
    return () => {
      videoThumbUrlBin.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
      videoThumbUrlBin.current.clear();
    };
  }, []);
  useEffect(() => {
    if (isGifControlled) {
      setGifList(props.gifList || []);
      if ((props.gifList || []).length) {
        setSelectedGifId(
          (prev) => props.gifList.find((g) => g.id === prev)?.id || props.gifList[0].id,
        );
      } else {
        setSelectedGifId(null);
      }
    }
  }, [props.gifList]);

  useEffect(() => {
    if (onGifLoadingRef.current && isGifControlled) {
      const hasUploadingGif = props.gifList?.some((gif) => gif.uploading === true);
      const hasBrokenGif = Object.values(gifPreviewErrors).some(Boolean);
      onGifLoadingRef.current(hasUploadingGif || hasBrokenGif || false);
    }
  }, [props.gifList, isGifControlled, gifPreviewErrors]);

  useEffect(() => {
    const currentGifIds = new Set(gifList.map((g) => g.id));
    setGifPreviewErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((id) => {
        if (!currentGifIds.has(id)) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setGifActualErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((id) => {
        if (!currentGifIds.has(id)) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [gifList]);

  const pushGifUpdate = (next) => {
    const resequenced = next.map((item, idx) => ({ ...item, index: idx }));
    setGifList(resequenced);
    props.onGifListUpdated && props.onGifListUpdated(resequenced);
  };
  const createGifURL = (file) => {
    const u = URL.createObjectURL(file);
    gifUrlBin.current.add(u);
    return u;
  };
  const revokeGifURL = (url) => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {}
    gifUrlBin.current.delete(url);
  };
  useEffect(() => {
    return () => {
      gifUrlBin.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
      gifUrlBin.current.clear();
    };
  }, []);

  const gifReplaceInputRef = useRef(null);
  const [gifReplaceTargetId, setGifReplaceTargetId] = useState(null);

  const openReplaceGifDialog = (id) => {
    setGifReplaceTargetId(id);
    setGifError('');
    if (!gifReplaceInputRef.current) return;
    gifReplaceInputRef.current.value = '';
    gifReplaceInputRef.current.click();
  };

  const onReplaceGifFileChange = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;

    const maxBytes = (allowedGifSizeMB || 10) * BYTES_IN_MB;
    if (!(file.type === 'image/gif' || /\.gif$/i.test(file.name)) || file.size > maxBytes) {
      setGifError(`Only .gif files allowed and size must be ≤ ${allowedGifSizeMB}MB.`);
      e.target.value = '';
      return;
    }
    setGifError('');

    const id = gifReplaceTargetId;
    if (!id) {
      e.target.value = '';
      return;
    }

    const blobUrl = createGifURL(file);
    const next = gifList.map((g) => {
      if (g.id !== id) return g;
      revokeGifURL(g.gifUrl);
      return {
        ...g,
        name: sanitizeFileName(file.name),
        size: file.size,
        file,
        gifUrl: blobUrl,
        url: blobUrl,
        uploadedAt: new Date().toISOString(),
        uploading: true,
      };
    });
    pushGifUpdate(next);
    setGifReplaceTargetId(null);
    e.target.value = '';
    try {
      if (ENVIRONMENT === 'dev') {
        console.log(`Uploading replacement GIF: ${file.name}`);
      }

      const uploadedKey = await uploadGifToCMS({
        file,
        groupId: props.groupId,
        propertyType: props.propertyType,
        hotelId: props.hotelId,
      });

      if (ENVIRONMENT === 'dev') {
        console.log(`Replacement GIF uploaded successfully: ${uploadedKey}`);
      }
      setGifList((currentList) => {
        const updatedList = currentList.map((g) =>
          g.id === id
            ? {
                ...g,
                gifUrl: uploadedKey,
                url: uploadedKey.startsWith('http') ? uploadedKey : `${ASSET_URL}${uploadedKey}`,
                file: null,
                uploading: false,
              }
            : g,
        );
        props.onGifListUpdated && props.onGifListUpdated(updatedList);
        return updatedList;
      });
      const serverUrl = uploadedKey.startsWith('http') ? uploadedKey : `${ASSET_URL}${uploadedKey}`;
      const isValid = await validateGifUrl(serverUrl);
      setGifPreviewErrors((prev) => ({ ...prev, [id]: !isValid }));
      setGifActualErrors((prev) => ({ ...prev, [id]: !isValid }));
      revokeGifURL(blobUrl);
    } catch (error) {
      if (ENVIRONMENT === 'dev') {
        console.error(`Error uploading replacement GIF ${file.name}:`, error);
      }
      setGifList((currentList) => {
        const updatedList = currentList.filter((g) => g.id !== id);
        props.onGifListUpdated && props.onGifListUpdated(updatedList);
        return updatedList;
      });
      revokeGifURL(blobUrl);

      notificationManager('danger', `Failed to upload ${file.name}`);
    }
  };

  const deleteGifRow = (id) => {
    const idx = gifList.findIndex((g) => g.id === id);
    const toDelete = gifList.find((g) => g.id === id);
    if (toDelete) {
      revokeGifURL(toDelete.gifUrl);
      revokeGifURL(toDelete.url);
    }

    const next = gifList.filter((g) => g.id !== id);
    pushGifUpdate(next);
    setGifError('');
    if (!next.length) setSelectedGifId(null);
    else setSelectedGifId(next[Math.min(idx, next.length - 1)].id);
    if (!next.length) clearGifThumbnail();
  };

  const onGifDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(gifList);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    pushGifUpdate(items);
    setGifError('');
  };

  const selectedGif = gifList.find((g) => g.id === selectedGifId);

  return (
    <>
      <ReactTooltip html={true} />
      <div className='row'>
        <div className='col-md-12 mb-3'>
          <Tabs
            value={mediaTab}
            onChange={onMediaTabChange}
            indicatorColor='primary'
            textColor='primary'
            className='media-tabs'
          >
            {enabledMediaTabs.includes(MEDIA_TABS.IMAGE) && (
              <Tab label='Image' value={MEDIA_TABS.IMAGE} />
            )}
            {enabledMediaTabs.includes(MEDIA_TABS.VIDEO) && (
              <Tab label='Video' value={MEDIA_TABS.VIDEO} />
            )}
            {enabledMediaTabs.includes(MEDIA_TABS.GIF) && (
              <Tab label='GIF' value={MEDIA_TABS.GIF} />
            )}
          </Tabs>
        </div>
      </div>
      {mediaTab === MEDIA_TABS.IMAGE && (
        <>
          <div className='tab-title'>Images</div>
          <div className='row mt-2'>
            <div className='col-md-5 img-upload'>
              <div className='row pt-2'>
                <div className='img-drop-zone'>
                  <div {...getRootProps({ style })}>
                    <input {...getInputProps()} />
                    <div className='d-flex align-items-center'>
                      {!props?.isSingleImage && (
                        <>
                          <Attachment />
                          <span className='ms-1'>
                            Drag and drop or{' '}
                            <span
                              className='text-highlight pointer'
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                open();
                              }}
                            >
                              Browse
                            </span>
                          </span>
                        </>
                      )}
                      {props?.isSingleImage && props.imgList[0] && (
                        <>
                          <div className='attached-doc-name mb-1 pe-2'>
                            {props.imgList[0]?.fileName}
                          </div>
                          <RemoveCircleRounded
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              removeCroppedImg();
                            }}
                            className='attached-doc-remove me-3'
                            color='secondary'
                          />
                        </>
                      )}
                      {props?.isSingleImage && props?.imgList?.length === 0 && (
                        <>
                          <Attachment />
                          <span className='ms-1'>
                            Drag and drop or{' '}
                            <span
                              className='text-highlight pointer'
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                open();
                              }}
                            >
                              Browse
                            </span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {imageError.status && (
                <div className='input-error mt-4'>
                  <Error className='me-1' fontSize='small' />
                  {imageError.message}
                </div>
              )}
              <div className='row img-upload-req gray-text mt-4'>
                <div className='img-upload-req-title'>Requirements</div>
                <div>Size of {props.allowedImageSize ? props.allowedImageSize : '10'}MB</div>
                <div>
                  Dimension of {props.resolutionWidth ? props.resolutionWidth : '4096'}W X{' '}
                  {props.resolutionHeight ? props.resolutionHeight : '2800'}H
                </div>
                <div>Format only in PNG, JPG, JPEG</div>
              </div>
            </div>

            <div className='col-md-7 img-preview'>
              <div
                className={`shadow img-preview-top-margin ${
                  !editingImage.url ? 'img-card p-2' : ''
                }`}
              >
                <div
                  className={`img-preview-cropper shadow ${
                    editingImage.image?.upImg ? 'image-added ' : ''
                  } ${
                    editingImage.url ? 'image-added' : 'img-card p-2 d-flex justify-content-center'
                  }`}
                >
                  <ReactCrop
                    className='re-crop shadow'
                    src={editingImage.url}
                    onImageLoaded={onLoad}
                    keepSelection={true}
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => {
                      onCropDragComplete(
                        c,
                        crop.aspect === 1 / 1
                          ? 'ratio1to1'
                          : crop.aspect === 16 / 9
                          ? 'ratio16to9'
                          : crop.aspect === 21 / 9
                          ? 'ratio21to9'
                          : crop.aspect === 9 / 16
                          ? 'ratio9to16'
                          : 'ratio9to21',
                      );
                    }}
                  />
                  {!editingImage.url && (
                    <div className='mb-4 text-center text-under-image'>
                      {props.imgList.length ? 'Select an image to crop' : 'Add an image to crop'}
                    </div>
                  )}
                </div>

                <div className='row mt-4 d-flex align-items-center'>
                  <div className='col-md-12 d-flex justify-content-start align-items-center'>
                    <span className='crop-label'>
                      Edit and save multiple crop ratios for an image.
                    </span>
                    <Tooltip
                      title='By defalut, all ratios are saved with center crop locations'
                      placement='top-end'
                      className='crop-label-tooltip'
                    >
                      <InfoIcon style={{ fontSize: '14px' }} className='ms-2' />
                    </Tooltip>
                  </div>
                </div>

                <div className='my-4 img-ratio-dropdown d-flex justify-content-between mx-2 align-items-center'>
                  <div className='d-flex justify-content-between align-items-center gap-3'>
                    <CustomSelect
                      item={{ value: selectedValue, validations: [], errors: {} }}
                      handleInputChange={(f) => {
                        const val = parseFloat(f.value);
                        setSelectedValue(val);
                        handleRatioChange(val);
                        if ([1 / 1, 16 / 9, 9 / 16, 21 / 9, 9 / 21].includes(val))
                          onDefaultCropperChange(val);
                      }}
                      options={imageRatios.map((r) => ({ label: r.label, value: r.value }))}
                      labelField='label'
                      valueField='value'
                      className='selectRatio'
                      customStyle
                      hideNullOption
                      bold
                    />
                    {editingImage.url && selectedRatio ? (
                      <div className='img-preview-crop-btns d-flex justify-content-center'>
                        {imageRatios.map((ratio) => (
                          <div
                            key={ratio.value}
                            onClick={() => onDefaultCropperChange(ratio.value)}
                            className={`${
                              editingImage.url && crop.aspect === ratio.value
                                ? 'image-border-highlight'
                                : 'd-none'
                            } d-flex align-items-end justify-content-center mx-2 my-2 text-white cropper-btn cropper-btn-gradient-background `}
                            style={{
                              backgroundImage: `url(${
                                editingImage.image?.fileData[
                                  'ratio' + ratio.label.replace(':', 'to')
                                ]?.imgUrl
                              })`,
                              backgroundSize: 'cover',
                              overflow: 'hidden',
                              width: 84,
                              height: 84,
                            }}
                          >
                            <span className='cropper-btn-ratio-text mb-2'>{ratio.label}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className='default-image-container'>
                        <img
                          src='../../assets/img/cropper-bg.svg'
                          alt='Default Image'
                          className='default-image'
                        />
                      </div>
                    )}
                  </div>

                  <div className='col-md-4 d-flex justify-content-end'>
                    <Button
                      className='save-crop-button'
                      color='primary'
                      variant='contained'
                      disabled={!editingImage.url}
                      onClick={() => handleSaveCrop(cropImgRef?.current)}
                    >
                      Save Crop
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {deleteImage?.status && (
            <DeletePopupModel
              deletePopUp={deleteImage}
              showImage
              onDelete={removeCroppedImg}
              onDeleteCancel={onDeleteCancel}
            />
          )}

          {!props?.isSingleImage && (
            <div className='row'>
              <div className='row mt-3'>
                <div>
                  <span className='gray-text'>
                    {props.imgList.length} {props.imgList.length === 1 ? 'Image' : 'Images'}{' '}
                    Uploaded
                  </span>
                  <Tooltip
                    title='Drag image thumbnail to manage sequence. First image in the sequence will be the cover image'
                    placement='right'
                  >
                    <InfoIcon style={{ fontSize: '12px' }} className='ms-4' />
                  </Tooltip>
                </div>
                <div className='d-flex mt-2'>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId='droppable' direction='horizontal'>
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          style={getListStyle(snapshot.isDraggingOver)}
                        >
                          {[...props.imgList]
                            .sort((v1, v2) => v1.index - v2.index)
                            .map((item, index) => (
                              <Draggable key={index} draggableId={'' + index} index={index}>
                                {(prov, snap) => (
                                  <div
                                    ref={prov.innerRef}
                                    {...prov.draggableProps}
                                    {...prov.dragHandleProps}
                                    style={getItemStyle(snap.isDragging, prov.draggableProps.style)}
                                    className='img-preview-cropped-img me-3'
                                  >
                                    <i
                                      className='fa fa-times-circle'
                                      onClick={() => onDeleteImageSelect(item, 'new')}
                                    ></i>
                                    <img
                                      alt={index}
                                      src={item.fileData?.master?.imgUrl}
                                      className={`${
                                        editingImage.index === index ? 'image-border-highlight' : ''
                                      }`}
                                      onClick={() => handleCropImageSelect(item, index)}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {mediaTab === MEDIA_TABS.VIDEO && (
        <div className='row'>
          <div className='col-md-7'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='tab-title'>Videos</div>
            </div>
            <div className='gray-text mb-2' style={{ fontSize: 12 }}>
              Paste video URLs(eg: mp4,.webm,YouTube)
            </div>
            <div className='mb-3 mt-3'>
              <Button color='primary' variant='outlined' onClick={addVideoRow}>
                Add
              </Button>
            </div>
            <DragDropContext onDragEnd={onVideoDragEnd}>
              <Droppable droppableId='video-droppable' direction='vertical'>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className='video-list-box'
                  >
                    {videoList.map((v, idx) => (
                      <Draggable key={v.id} draggableId={v.id} index={idx}>
                        {(prov, snap) => {
                          const isSelected = selectedVideoId === v.id;

                          return (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              className='custom-duration-box mb-2'
                              style={{
                                background: '#FBFBFB',
                                border:
                                  isSelected || snap.isDragging
                                    ? '2px solid #03E67C'
                                    : '1px solid #EAEAEA',
                                borderRadius: 8,
                                cursor: 'pointer',
                                ...prov.draggableProps.style,
                              }}
                              onClick={() => setSelectedVideoId(v.id)}
                            >
                              <div className='d-flex align-items-center p-3'>
                                <span
                                  {...prov.dragHandleProps}
                                  className='me-2'
                                  title='Drag to reorder'
                                >
                                  <DragIndicator style={{ color: '#9E9E9E' }} />
                                </span>

                                <TextField
                                  id={`video-url-${v.id}`}
                                  value={v.url}
                                  onChange={(e) => updateVideoUrl(v.id, e.target.value)}
                                  placeholder='Ex: https://video.mp4'
                                  fullWidth
                                  variant='standard'
                                  onFocus={() => setSelectedVideoId(v.id)}
                                  error={videoFormatErrors[v.id]}
                                />

                                <Tooltip title='Remove'>
                                  <IconButton
                                    className='ms-1'
                                    onClick={() => deleteVideoRow(v.id)}
                                    size='small'
                                    disabled={videoList.length === 1 && !v.url}
                                    style={{ marginLeft: '20px !important' }}
                                  >
                                    <RemoveCircle />
                                  </IconButton>
                                </Tooltip>
                              </div>

                              {videoFormatErrors[v.id] && (
                                <div
                                  className='input-error px-3'
                                  style={{ fontSize: 12, marginLeft: 30, marginTop: 4 }}
                                >
                                  Only MP4, WEBM, and YouTube videos are supported
                                </div>
                              )}

                              <div
                                className='px-3'
                                style={{ fontSize: 11, color: '#888', marginLeft: 30 }}
                              >
                                {v.url ? `` : 'Enter URL to upload'}
                              </div>
                            </div>
                          );
                        }}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          <div className='col-md-5 mt-1'>
            <div className='tab-title'>Playback options</div>
            <div className='playback-row'>
              <CustomSelect
                item={{
                  value: selectedVideo?.loopType ?? LOOP_TYPES.ALWAYS,
                  validations: [],
                  errors: {},
                }}
                handleInputChange={handleLoopTypeChange}
                options={[
                  { label: 'Always Loop', value: LOOP_TYPES.ALWAYS },
                  { label: 'Loop Count', value: LOOP_TYPES.COUNT },
                ]}
                labelField='label'
                valueField='value'
                label='Loop Count'
                className='playback-select'
                customStyle
                hideNullOption
                disabled={!selectedVideo}
              />
              <TextField
                type='number'
                variant='standard'
                className='playback-count'
                value={
                  selectedVideo?.loopType === LOOP_TYPES.COUNT
                    ? selectedVideo?.loopCount === ''
                      ? ''
                      : String(selectedVideo?.loopCount ?? '1')
                    : '1'
                }
                onChange={handleLoopCountChange}
                onBlur={clampLoopCountOnBlur}
                disabled={!selectedVideo || selectedVideo?.loopType !== LOOP_TYPES.COUNT}
                InputProps={{ inputProps: { min: 1, step: 1 } }}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-', '.', 'Enter'].includes(e.key)) e.preventDefault();
                }}
              />
            </div>
            <div className='tab-title mb-2 mt-4'>Preview</div>
            {selectedVideo ? (
              <>
                <VideoPreview
                  url={selectedVideo.url}
                  uploadedAt={selectedVideo.uploadedAt}
                  onError={(errorMessage) => {
                    setVideoErrors((prev) => ({
                      ...prev,
                      [selectedVideo.id]: !!errorMessage,
                    }));
                  }}
                />
                {selectedVideo.loopType === LOOP_TYPES.COUNT && (
                  <VideoThumbUploader
                    thumb={
                      videoThumbPreview || (videoImageUrl ? `${ASSET_URL}${videoImageUrl}` : '')
                    }
                    tooltipTitle='Display image after Video playback'
                    uploading={videoThumbUploading}
                    onChange={async (file) => {
                      const patchPreview = (val) => setVideoThumbPreview(val || '');
                      if (!file) {
                        patchPreview('');
                        onVideoImageUrlChange && onVideoImageUrlChange('');
                        return;
                      }
                      const previewUrl = createThumbURL(file, videoThumbUrlBin);
                      patchPreview(previewUrl);
                      setVideoThumbUploading(true);
                      try {
                        const key = await uploadSingleImageViaCMS({
                          file,
                          groupId: props.groupId,
                          propertyType: props.propertyType,
                          hotelId: props.hotelId,
                        });
                        onVideoImageUrlChange && onVideoImageUrlChange(key);
                      } catch (err) {
                        patchPreview('');
                        notificationManager('danger', err.message || 'Thumbnail upload failed');
                      } finally {
                        setVideoThumbUploading(false);
                      }
                    }}
                  />
                )}
              </>
            ) : (
              <>
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{ minHeight: 180, border: '1px dashed #CCCCCC', borderRadius: 10 }}
                >
                  <span className='gray-text'>Select a video to preview</span>
                </div>
                <SupportedFormatsNote
                  types='.mp4, .webm, YouTube'
                  size='10MB'
                  className='mt-2'
                  hideMaxSize
                />
              </>
            )}
          </div>
        </div>
      )}

      {mediaTab === MEDIA_TABS.GIF && (
        <div className='row align-items-start'>
          <div className='col-md-7'>
            <div className='tab-title mb-2'>GIFs</div>
            <div className='gray-text mb-2' style={{ fontSize: 12 }}>
              Upload one or more .gif files.
            </div>
            <GifDropArea
              onAdd={addGifFiles}
              onError={(msg) => setGifError(msg)}
              maxMB={allowedGifSizeMB}
            />

            <DragDropContext onDragEnd={onGifDragEnd}>
              <Droppable droppableId='gif-droppable' direction='vertical'>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {gifList.map((g, idx) => (
                      <Draggable key={g.id} draggableId={g.id} index={idx}>
                        {(prov, snap) => {
                          const isSelected = selectedGifId === g.id;

                          return (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              className='mb-2 p-3'
                              style={{
                                background: '#FBFBFB',
                                border:
                                  isSelected || snap.isDragging
                                    ? '2px solid #03E67C'
                                    : '1px solid #EAEAEA',
                                borderRadius: 8,
                                ...prov.draggableProps.style,
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setSelectedGifId(g.id);
                                setGifError('');
                              }}
                            >
                              <div className='d-flex align-items-center'>
                                <span
                                  {...prov.dragHandleProps}
                                  className='me-2'
                                  title='Drag to reorder'
                                  style={{ flexShrink: 0 }}
                                >
                                  <DragIndicator style={{ color: '#9E9E9E' }} />
                                </span>

                                <div
                                  className='gif-row-name flex-grow-1'
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                  }}
                                  title={g.name}
                                >
                                  {g.name}
                                </div>

                                <div style={{ display: 'flex', flexShrink: 0, marginLeft: '8px' }}>
                                  <Tooltip title='Replace'>
                                    <IconButton
                                      size='small'
                                      disabled={g.uploading}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openReplaceGifDialog(g.id);
                                      }}
                                    >
                                      <Loop />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title='Delete'>
                                    <IconButton
                                      size='small'
                                      disabled={g.uploading}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteGifRow(g.id);
                                      }}
                                    >
                                      <RemoveCircle />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </div>

                              <div
                                className='mt-1'
                                style={{ fontSize: 11, color: '#888', marginLeft: 32 }}
                              >
                                {fmtSize(g.size)}
                              </div>
                            </div>
                          );
                        }}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <input
              ref={gifReplaceInputRef}
              type='file'
              accept='image/gif'
              style={{ display: 'none' }}
              onChange={onReplaceGifFileChange}
            />
          </div>

          <div style={{ marginTop: '34px' }} className='col-md-5'>
            <div className='tab-title mb-2'>Preview</div>

            {selectedGif ? (
              <>
                <GifPreview
                  url={resolveGifSrc(selectedGif)}
                  uploadedAt={selectedGif.uploadedAt}
                  uploading={selectedGif.uploading}
                  onError={(hasIssue, isActualError) => {
                    setGifPreviewErrors((prev) => ({
                      ...prev,
                      [selectedGif.id]: hasIssue,
                    }));
                    setGifActualErrors((prev) => ({
                      ...prev,
                      [selectedGif.id]: !!isActualError,
                    }));
                  }}
                />
                {gifActualErrors[selectedGif.id] && (
                  <div className='input-error mt-2' style={{ maxWidth: 320 }}>
                    Unable to load preview. Please remove the broken GIF and try again.
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{ minHeight: 180, border: '1px dashed #CCCCCC', borderRadius: 10 }}
                >
                  <span className='gray-text'>Select a GIF to preview</span>
                </div>
                {gifList.length === 0 && (
                  <SupportedFormatsNote
                    types='GIF'
                    size={`${allowedGifSizeMB}MB`}
                    className='mt-2'
                  />
                )}
              </>
            )}
            {gifError && (
              <div className='input-error mt-2' style={{ maxWidth: 320 }}>
                {gifError}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageUpload;
